import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import * as esri from 'esri-leaflet';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents, ZoomControl } from 'react-leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import {
  identifyMapParcel,
  type MapConfig,
  type ParcelIdentifyResult,
  type ParcelSelectionMode,
} from '@/lib/api/map';
import {
  centroidFromGeometry,
  leafletLayerToGeoJsonPolygon,
} from '@/lib/map/geometry';
import {
  buildCompilationLayerDefs,
  IPLAN_COMPILATION_LAYER_ID,
} from '@/lib/map/compilation-layer-def';
import { useMapConfig } from '@/lib/map/useMapConfig';
import {
  EASTERN_GALILEE_CENTER,
  EASTERN_GALILEE_DEFAULT_ZOOM,
  EASTERN_GALILEE_EXPANDED_MAX_ZOOM,
  EASTERN_GALILEE_LEAFLET_BOUNDS,
  EASTERN_GALILEE_MAX_ZOOM,
  EASTERN_GALILEE_MIN_ZOOM,
  CLUSTER_OUT_OF_BOUNDS_MESSAGE,
  isGeometryWithinEasternGalilee,
  isWithinEasternGalilee,
  type SettlementCentroid,
} from '@/lib/map/eastern-galilee';
import ClusterBoundaryLayer from './ClusterBoundaryLayer';
import AerialReferenceOverlay from './AerialReferenceOverlay';
import SettlementLabelLayer from './SettlementLabelLayer';
import SettlementMapSearch, {
  type MapSearchFocusTarget,
  type MapSettlementOption,
} from './SettlementMapSearch';
import { useTranslation } from '@/i18n/useTranslation';
import {
  FALLBACK_BASEMAP,
  MapClickModeIcon,
  MapDrawModeIcon,
  MapSizeIcon,
  resolveBasemap,
  type MapBasemapKind,
} from './map-basemap.util';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import styles from './EasternGalileeMapPicker.module.css';

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

export interface EasternGalileeMapPickerProps {
  lat?: number;
  lng?: number;
  parcelGeometry?: { type: string; coordinates: unknown } | null;
  selectionMode?: ParcelSelectionMode;
  onSelectionModeChange?: (mode: ParcelSelectionMode) => void;
  onParcelIdentified: (result: ParcelIdentifyResult) => void;
  onIdentifyError?: (message: string) => void;
  hint?: string;
  drawHint?: string;
  selectedLabel?: string;
  error?: string;
  mapAriaLabel: string;
  identifyError?: string;
  /** Drop outer map border when nested inside MapScreen `.mapUnit`. */
  embedded?: boolean;
  /** TYPE_CODE values to render on the compilation layer; omit when all visible. */
  enabledDesignationCodes?: string[];
  allDesignationCodes?: string[];
  /**
   * When false, GIS layers and legend are hidden. On point click, lat/lng is
   * returned directly without a parcel identify API call. Defaults to true.
   */
  layersEnabled?: boolean;
  /** Initial basemap when `showBasemapToggle` is true. Defaults to streets. */
  defaultBasemap?: MapBasemapKind;
  /** Show מפה / תצלום אוויר toggle (AgriTour Step B). */
  showBasemapToggle?: boolean;
  /** Settlement list for search + aerial labels (AgriTour). */
  settlements?: MapSettlementOption[];
  /** Fly map to settlement coords when Step A selection changes (no auto-pin). */
  focusSettlement?: { lat: number; lng: number } | null;
  /** Step A settlementItemId — syncs search field + flyTo */
  selectedSettlementId?: string;
}

function MapGisLayers({
  config,
  onCompilationState,
  enabledDesignationCodes,
  allDesignationCodes,
}: {
  config: MapConfig;
  onCompilationState?: (loading: boolean) => void;
  enabledDesignationCodes?: string[];
  allDesignationCodes?: string[];
}) {
  const map = useMap();
  const layersRef = useRef<L.Layer[]>([]);
  const dynamicRef = useRef<ReturnType<typeof esri.dynamicMapLayer> | null>(null);
  const compilation = config.layers.compilation;
  const layerId = compilation?.visibleLayers?.[0] ?? IPLAN_COMPILATION_LAYER_ID;

  const enabledKey = (enabledDesignationCodes ?? []).join(',');
  const allKey = (allDesignationCodes ?? []).join(',');

  const layerDefs = useMemo(() => {
    if (!allDesignationCodes?.length) {
      return false;
    }
    return buildCompilationLayerDefs(
      layerId,
      enabledDesignationCodes ?? [],
      allDesignationCodes,
    );
  }, [layerId, enabledKey, allKey, enabledDesignationCodes, allDesignationCodes]);

  useEffect(() => {
    for (const layer of layersRef.current) {
      map.removeLayer(layer);
    }
    layersRef.current = [];
    dynamicRef.current = null;

    if (!compilation) {
      onCompilationState?.(false);
      return;
    }

    const compilationUrl = `${compilation.proxyBase}/${compilation.mapServerPath}`;
    const dynamic = esri.dynamicMapLayer({
      url: compilationUrl,
      layers: compilation.visibleLayers,
      opacity: compilation.opacity,
      updateInterval: 300,
      transparent: true,
    });

    const handleLoading = () => onCompilationState?.(true);
    const handleReady = () => onCompilationState?.(false);
    dynamic.on('loading', handleLoading);
    dynamic.on('load', handleReady);
    dynamic.on('error', handleReady);

    onCompilationState?.(true);
    dynamic.addTo(map);
    layersRef.current.push(dynamic);
    dynamicRef.current = dynamic;

    return () => {
      dynamic.off('loading', handleLoading);
      dynamic.off('load', handleReady);
      dynamic.off('error', handleReady);
      for (const layer of layersRef.current) {
        map.removeLayer(layer);
      }
      layersRef.current = [];
      dynamicRef.current = null;
      onCompilationState?.(false);
    };
  }, [compilation, map, onCompilationState]);

  useEffect(() => {
    const dynamic = dynamicRef.current;
    if (!dynamic) {
      return;
    }
    dynamic.setLayerDefs(layerDefs);
  }, [layerDefs]);

  return null;
}

type LeafletMapWithPm = L.Map & {
  pm?: {
    setGlobalOptions: (o: { snappable?: boolean; snapDistance?: number }) => void;
    enableDraw: (shape: string, options?: { snappable?: boolean }) => void;
    disableDraw: () => void;
  };
};

function mapHasPm(map: L.Map): map is LeafletMapWithPm {
  return (map as LeafletMapWithPm).pm != null;
}

function ParcelHighlight({
  geometry,
  drawn = false,
}: {
  geometry?: { type: string; coordinates: unknown } | null;
  drawn?: boolean;
}) {
  const map = useMap();
  const layerRef = useRef<L.GeoJSON | null>(null);

  useEffect(() => {
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
    if (!geometry) return;

    const layer = L.geoJSON(geometry as GeoJSON.GeoJsonObject, {
      style: drawn
        ? {
            color: '#c2410c',
            weight: 3,
            dashArray: '6 4',
            fillColor: '#ea580c',
            fillOpacity: 0.2,
          }
        : {
            color: '#1f6feb',
            weight: 3,
            fillColor: '#1f6feb',
            fillOpacity: 0.15,
          },
    });
    layer.addTo(map);
    layerRef.current = layer;

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [geometry, map, drawn]);

  return null;
}

function MapDrawControl({
  skipApiCall = false,
  onIdentified,
  onIdentifying,
  onIdentifyError,
}: {
  skipApiCall?: boolean;
  onIdentified: (result: ParcelIdentifyResult) => void;
  onIdentifying: (loading: boolean) => void;
  onIdentifyError?: (message: string) => void;
}) {
  const map = useMap();
  const drawLayerRef = useRef<L.Layer | null>(null);
  const identifyingRef = useRef(false);

  const clearDrawLayer = useCallback(() => {
    if (drawLayerRef.current) {
      map.removeLayer(drawLayerRef.current);
      drawLayerRef.current = null;
    }
  }, [map]);

  useEffect(() => {
    if (!mapHasPm(map)) {
      onIdentifyError?.('לא ניתן לטעון כלי ציור על המפה');
      return;
    }

    map.pm.setGlobalOptions({
      snappable: true,
      snapDistance: 12,
    });
    map.pm.enableDraw('Polygon', { snappable: true });

    return () => {
      map.pm.disableDraw();
      clearDrawLayer();
    };
  }, [map, clearDrawLayer, onIdentifyError]);

  useEffect(() => {
    if (!mapHasPm(map)) return;

    const handleCreate = async (event: { layer: L.Layer }) => {
      const layer = event.layer;
      if (!(layer instanceof L.Polygon)) return;

      clearDrawLayer();
      drawLayerRef.current = layer;
      layer.addTo(map);

      const geometry = leafletLayerToGeoJsonPolygon(layer);
      if (!geometry) return;

      if (!isGeometryWithinEasternGalilee(geometry)) {
        onIdentifyError?.(CLUSTER_OUT_OF_BOUNDS_MESSAGE);
        clearDrawLayer();
        return;
      }

      const center = centroidFromGeometry(geometry);

      if (skipApiCall) {
        onIdentified({
          migrash: undefined,
          gush: undefined,
          helka: undefined,
          designation: undefined,
          lat: center.lat,
          lng: center.lng,
          geometry,
          selection_mode: 'polygon_drawn',
          source: 'basemap_draw',
        });
        return;
      }

      if (identifyingRef.current) return;
      identifyingRef.current = true;
      onIdentifying(true);
      try {
        const result = await identifyMapParcel(center.lat, center.lng, geometry);
        onIdentified(result);
      } catch (err) {
        onIdentifyError?.(err instanceof Error ? err.message : 'Identify failed');
        clearDrawLayer();
      } finally {
        identifyingRef.current = false;
        onIdentifying(false);
      }
    };

    map.on('pm:create', handleCreate);
    return () => {
      map.off('pm:create', handleCreate);
    };
  }, [map, skipApiCall, clearDrawLayer, onIdentified, onIdentifying, onIdentifyError]);

  return null;
}

function MapClickIdentify({
  enabled,
  skipApiCall = false,
  onIdentified,
  onIdentifying,
  onIdentifyError,
}: {
  enabled: boolean;
  skipApiCall?: boolean;
  onIdentified: (result: ParcelIdentifyResult) => void;
  onIdentifying: (loading: boolean) => void;
  onIdentifyError?: (message: string) => void;
}) {
  const identifyingRef = useRef(false);

  const handleClick = useCallback(
    async (lat: number, lng: number) => {
      if (identifyingRef.current) return;
      if (!isWithinEasternGalilee(lat, lng)) {
        onIdentifyError?.(CLUSTER_OUT_OF_BOUNDS_MESSAGE);
        return;
      }
      if (skipApiCall) {
        onIdentified({
          migrash: undefined,
          gush: undefined,
          helka: undefined,
          designation: undefined,
          lat,
          lng,
          geometry: undefined,
          selection_mode: 'point',
          source: 'basemap_click',
        });
        return;
      }
      identifyingRef.current = true;
      onIdentifying(true);
      try {
        const result = await identifyMapParcel(lat, lng);
        onIdentified(result);
      } catch (err) {
        onIdentifyError?.(err instanceof Error ? err.message : 'Identify failed');
      } finally {
        identifyingRef.current = false;
        onIdentifying(false);
      }
    },
    [skipApiCall, onIdentified, onIdentifying, onIdentifyError],
  );

  useMapEvents({
    click(event) {
      if (!enabled) return;
      void handleClick(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

const SETTLEMENT_FOCUS_ZOOM = 14;

function MapFlyToSettlement({
  focus,
  focusToken = 0,
}: {
  focus?: { lat: number; lng: number } | null;
  /** Bumps on each explicit search / Step A sync so the same coords can refocus. */
  focusToken?: number;
}) {
  const map = useMap();
  const lastApplied = useRef<string | null>(null);

  useEffect(() => {
    if (!focus || !Number.isFinite(focus.lat) || !Number.isFinite(focus.lng)) return;
    const key = `${focusToken}:${focus.lat.toFixed(5)},${focus.lng.toFixed(5)}`;
    if (lastApplied.current === key) return;
    lastApplied.current = key;

    const target = L.latLng(focus.lat, focus.lng);
    const run = () => {
      map.invalidateSize({ animate: false });
      map.flyTo(target, Math.max(SETTLEMENT_FOCUS_ZOOM, map.getZoom()), {
        animate: true,
        duration: 0.65,
      });
    };

    if ((map as L.Map & { _loaded?: boolean })._loaded) {
      run();
    } else {
      map.whenReady(run);
    }
  }, [focus, focusToken, map]);

  return null;
}

function MapViewSync({ lat, lng }: { lat?: number; lng?: number }) {
  const map = useMap();
  const lastSynced = useRef<string | null>(null);

  useEffect(() => {
    if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }
    const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    if (lastSynced.current === key) return;
    lastSynced.current = key;

    const center = map.getCenter();
    const distM = map.distance(center, L.latLng(lat, lng));
    if (distM < 80) return;

    map.setView([lat, lng], Math.max(map.getZoom(), 13), { animate: false });
  }, [lat, lng, map]);
  return null;
}

const MAP_PANEL_RESIZE_MS = 220;

/** After expand/collapse, reflow tiles; optionally fit cluster bounds (GIS wizard only). */
function MapFitOnPanelResize({
  expanded,
  fitClusterOnResize = true,
}: {
  expanded: boolean;
  fitClusterOnResize?: boolean;
}) {
  const map = useMap();
  const skipInitial = useRef(true);

  useEffect(() => {
    if (skipInitial.current) {
      skipInitial.current = false;
      const initial = window.setTimeout(() => map.invalidateSize(), 0);
      return () => window.clearTimeout(initial);
    }

    const fit = () => {
      map.invalidateSize({ animate: false });
      if (!fitClusterOnResize) return;
      map.fitBounds(EASTERN_GALILEE_LEAFLET_BOUNDS, {
        padding: expanded ? [56, 56] : [28, 28],
        maxZoom: expanded
          ? EASTERN_GALILEE_EXPANDED_MAX_ZOOM
          : EASTERN_GALILEE_DEFAULT_ZOOM,
        animate: true,
        duration: 0.35,
      });
    };

    const timer = window.setTimeout(fit, MAP_PANEL_RESIZE_MS);
    return () => window.clearTimeout(timer);
  }, [expanded, fitClusterOnResize, map]);

  return null;
}

export default function EasternGalileeMapPicker({
  lat,
  lng,
  parcelGeometry,
  selectionMode: selectionModeProp,
  onSelectionModeChange,
  onParcelIdentified,
  onIdentifyError,
  hint = '',
  drawHint,
  selectedLabel,
  error,
  identifyError,
  mapAriaLabel,
  embedded = false,
  enabledDesignationCodes,
  allDesignationCodes,
  layersEnabled = true,
  defaultBasemap = 'streets',
  showBasemapToggle = false,
  settlements = [],
  focusSettlement = null,
  selectedSettlementId,
}: EasternGalileeMapPickerProps) {
  const t = useTranslation();
  const { config, error: configError } = useMapConfig();
  const [internalMode, setInternalMode] = useState<ParcelSelectionMode>('point');
  const selectionMode = selectionModeProp ?? internalMode;
  const setSelectionMode = onSelectionModeChange ?? setInternalMode;
  const [basemapKind, setBasemapKind] = useState<MapBasemapKind>(defaultBasemap);
  const [identifying, setIdentifying] = useState(false);
  const [compilationLoading, setCompilationLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const handleCompilationState = useCallback((loading: boolean) => {
    setCompilationLoading(loading);
  }, []);

  const position = useMemo(() => {
    if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }
    return [lat, lng] as [number, number];
  }, [lat, lng]);

  const [searchFocus, setSearchFocus] = useState<{ lat: number; lng: number } | null>(null);
  const [focusRequestId, setFocusRequestId] = useState(0);
  const flyTarget = searchFocus ?? focusSettlement;

  const labelSettlements = useMemo((): SettlementCentroid[] => {
    return settlements
      .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng))
      .map((s) => ({ name: s.name, lat: s.lat!, lng: s.lng! }));
  }, [settlements]);

  const handleSettlementFocus = useCallback((target: MapSearchFocusTarget) => {
    setSearchFocus({ lat: target.lat, lng: target.lng });
    setFocusRequestId((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!focusSettlement) return;
    setFocusRequestId((value) => value + 1);
  }, [focusSettlement?.lat, focusSettlement?.lng]);

  const basemap = showBasemapToggle
    ? resolveBasemap(basemapKind)
    : (config?.layers.basemap ?? FALLBACK_BASEMAP);
  const sizeToggleLabel = expanded ? t.map.collapseMap : t.map.expandMap;

  const modeToolbar = (
    <>
      <button
        type="button"
        className={selectionMode === 'point' ? styles.modeActive : styles.modeButton}
        aria-pressed={selectionMode === 'point'}
        aria-label={t.map.clickToSelect}
        title={t.map.clickToSelect}
        onClick={() => setSelectionMode('point')}
      >
        <span className={styles.modeButtonContent}>
          <MapClickModeIcon />
          <span className={styles.modeButtonLabel}>{t.map.clickToSelect}</span>
          {showBasemapToggle ? (
            <span className={styles.modeButtonLabelCompact} aria-hidden="true">
              נקודה
            </span>
          ) : null}
        </span>
      </button>
      <button
        type="button"
        className={
          selectionMode === 'polygon_drawn' ? styles.modeActive : styles.modeButton
        }
        aria-pressed={selectionMode === 'polygon_drawn'}
        aria-label={t.map.drawPolygon}
        title={t.map.drawPolygon}
        onClick={() => setSelectionMode('polygon_drawn')}
      >
        <span className={styles.modeButtonContent}>
          <MapDrawModeIcon />
          <span className={styles.modeButtonLabel}>{t.map.drawPolygon}</span>
          {showBasemapToggle ? (
            <span className={styles.modeButtonLabelCompact} aria-hidden="true">
              שרטוט
            </span>
          ) : null}
        </span>
      </button>
    </>
  );

  return (
    <div
      className={`${styles.wrap} ${embedded ? styles.wrapEmbedded : ''}`.trim()}
      data-testid="eastern-galilee-map"
    >
      {hint && !showBasemapToggle ? (
        <p className={styles.hint}>{selectionMode === 'point' ? hint : drawHint ?? t.map.drawHint}</p>
      ) : null}
      {configError ? (
        <p className={styles.error} role="alert">
          {configError}
        </p>
      ) : null}
      <div
        className={`${styles.mapFrame} ${expanded ? styles.mapFrameExpanded : ''} ${showBasemapToggle ? styles.mapFrameAgriTour : ''} ${showBasemapToggle ? styles.mapFrameAgriTourColumn : ''} ${showBasemapToggle && selectionMode === 'polygon_drawn' ? styles.mapFrameAgriTourDraw : ''}`.trim()}
        role="application"
        aria-label={mapAriaLabel}
        data-expanded={expanded ? 'true' : 'false'}
        data-testid="map-frame"
      >
        {showBasemapToggle && settlements.length > 0 ? (
          <div className={styles.mapChrome}>
            <SettlementMapSearch
              settlements={settlements}
              onFocus={handleSettlementFocus}
              selectedSettlementId={selectedSettlementId}
            />
          </div>
        ) : null}
        <div className={showBasemapToggle ? styles.mapSurface : undefined}>
        {!showBasemapToggle ? (
          <div
            className={styles.modeToolbar}
            role="group"
            aria-label={t.map.selectionModeGroupLabel}
            data-testid="map-selection-mode"
          >
            {modeToolbar}
          </div>
        ) : null}
        {!showBasemapToggle ? (
          <button
            type="button"
            className={styles.sizeToggle}
            onClick={() => setExpanded((value) => !value)}
            aria-pressed={expanded}
            aria-label={sizeToggleLabel}
            title={sizeToggleLabel}
            data-testid="map-size-toggle"
          >
            <MapSizeIcon expanded={expanded} />
          </button>
        ) : null}
        <MapContainer
          center={position ?? EASTERN_GALILEE_CENTER}
          zoom={EASTERN_GALILEE_DEFAULT_ZOOM}
          minZoom={EASTERN_GALILEE_MIN_ZOOM}
          maxZoom={EASTERN_GALILEE_MAX_ZOOM}
          maxBounds={EASTERN_GALILEE_LEAFLET_BOUNDS}
          maxBoundsViscosity={1}
          scrollWheelZoom
          zoomAnimation={false}
          fadeAnimation={false}
          zoomControl={false}
          className={styles.map}
        >
          <ZoomControl position={showBasemapToggle ? 'bottomleft' : 'bottomright'} />
          <TileLayer
            attribution={basemap.attribution ?? FALLBACK_BASEMAP.attribution}
            url={basemap.url}
            subdomains={'subdomains' in basemap ? basemap.subdomains : FALLBACK_BASEMAP.subdomains}
            detectRetina
            updateWhenIdle
            updateWhenZooming={false}
            keepBuffer={2}
          />
          {showBasemapToggle && basemapKind === 'aerial' ? <AerialReferenceOverlay /> : null}
          {import.meta.env.MODE !== 'test' ? <ClusterBoundaryLayer /> : null}
          {showBasemapToggle && basemapKind === 'aerial' && labelSettlements.length > 0 ? (
            <SettlementLabelLayer settlements={labelSettlements} />
          ) : null}
          {layersEnabled && config?.layers.compilation ? (
            <MapGisLayers
              config={config}
              onCompilationState={handleCompilationState}
              enabledDesignationCodes={enabledDesignationCodes}
              allDesignationCodes={allDesignationCodes}
            />
          ) : null}
          <MapClickIdentify
            enabled={selectionMode === 'point'}
            skipApiCall={!layersEnabled}
            onIdentified={onParcelIdentified}
            onIdentifying={setIdentifying}
            onIdentifyError={onIdentifyError}
          />
          {selectionMode === 'polygon_drawn' ? (
            <MapDrawControl
              skipApiCall={!layersEnabled}
              onIdentified={onParcelIdentified}
              onIdentifying={setIdentifying}
              onIdentifyError={onIdentifyError}
            />
          ) : null}
          <MapFlyToSettlement focus={flyTarget} focusToken={focusRequestId} />
          <MapViewSync lat={lat} lng={lng} />
          <MapFitOnPanelResize expanded={expanded} fitClusterOnResize={!showBasemapToggle} />
          <ParcelHighlight
            geometry={parcelGeometry}
            drawn={selectionMode === 'polygon_drawn'}
          />
          {position ? <Marker position={position} /> : null}
        </MapContainer>
        {compilationLoading ? (
          <div className={styles.layersLoading} data-testid="map-layers-loading">
            {t.map.layersLoading}
          </div>
        ) : null}
        {identifying ? (
          <div className={styles.loadingOverlay} data-testid="map-identifying">
            מזהה ייעוד…
          </div>
        ) : null}
        {embedded && selectedLabel && position ? (
          <div className={styles.selectionChip} data-testid="map-selection-label">
            {selectedLabel}
          </div>
        ) : null}
        {embedded && layersEnabled && config?.attribution ? (
          <div className={styles.attribBar}>{config.attribution}</div>
        ) : null}
        </div>
        {showBasemapToggle ? (
          <div className={styles.mapControlsDock}>
            <div
              className={`${styles.controlGroup} ${styles.controlGroupBasemap}`}
              role="group"
              aria-label="סוג מפה"
              data-testid="map-basemap-toggle"
            >
              <button
                type="button"
                className={basemapKind === 'streets' ? styles.modeActive : styles.modeButton}
                aria-pressed={basemapKind === 'streets'}
                aria-label="מפת רחובות"
                onClick={() => setBasemapKind('streets')}
              >
                <span className={styles.modeButtonLabel}>מפה</span>
              </button>
              <button
                type="button"
                className={basemapKind === 'aerial' ? styles.modeActive : styles.modeButton}
                aria-pressed={basemapKind === 'aerial'}
                aria-label="תצלום אוויר"
                onClick={() => setBasemapKind('aerial')}
              >
                <span className={styles.modeButtonLabelFull}>תצלום אוויר</span>
                <span className={styles.modeButtonLabelShort} aria-hidden="true">
                  אוויר
                </span>
              </button>
            </div>
            <div
              className={`${styles.controlGroup} ${styles.controlGroupMode}`}
              role="group"
              aria-label={t.map.selectionModeGroupLabel}
              data-testid="map-selection-mode"
            >
              {modeToolbar}
            </div>
            <button
              type="button"
              className={styles.dockSizeToggle}
              onClick={() => setExpanded((value) => !value)}
              aria-pressed={expanded}
              aria-label={sizeToggleLabel}
              title={sizeToggleLabel}
              data-testid="map-size-toggle"
            >
              <MapSizeIcon expanded={expanded} />
              <span className={styles.dockSizeToggleLabel}>{expanded ? 'צמצם' : 'הרחב'}</span>
            </button>
          </div>
        ) : null}
      </div>
      {!embedded && selectedLabel && position ? (
        <p className={styles.selected} data-testid="map-selection-label">
          {selectedLabel}
        </p>
      ) : null}
      {!embedded && layersEnabled && config?.attribution ? (
        <p className={styles.attribution}>{config.attribution}</p>
      ) : null}
      {identifyError ? (
        <span role="alert" className={styles.error} data-testid="map-identify-error">
          {identifyError}
        </span>
      ) : null}
      {error ? (
        <span role="alert" className={styles.error} data-testid="map-location-error">
          {error}
        </span>
      ) : null}
    </div>
  );
}
